import React, { createContext, useContext, useState, useCallback } from 'react';

// Translation data for all supported languages
const translations = {
  en: {
    // Common
    welcome: "Welcome",
    signIn: "Sign In",
    signUp: "Sign Up",
    signOut: "Sign Out",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm Password",
    name: "Name",
    phone: "Phone",
    save: "Save",
    cancel: "Cancel",
    back: "Back",
    next: "Next",
    loading: "Loading...",
    search: "Search",
    cart: "Cart",
    checkout: "Checkout",
    total: "Total",
    subtotal: "Subtotal",
    tax: "Tax",
    quantity: "Quantity",
    item: "Item",
    items: "Items",
    home: "Home",
    shop: "Shop",
    account: "Account",
    settings: "Settings",
    help: "Help",
    about: "About",
    contact: "Contact",
    
    // Auth
    createAccount: "Create Account",
    forgotPassword: "Forgot Password?",
    resetPassword: "Reset Password",
    sendResetCode: "Send Reset Code",
    enterEmail: "Enter your email",
    enterPassword: "Enter your password",
    alreadyHaveAccount: "Already have an account?",
    dontHaveAccount: "Don't have an account?",
    passwordResetSent: "Password reset code sent to your email",
    loginSuccess: "Welcome back!",
    logoutSuccess: "You have been logged out",
    accountCreated: "Account created successfully!",
    invalidCredentials: "Invalid email or password",
    
    // Language
    selectLanguage: "Select Language",
    language: "Language",
    
    // Categories
    allCategories: "All Categories",
    all: "All",
    produce: "Produce",
    meats: "Meats",
    seafood: "Seafood",
    spices: "Spices",
    grains: "Grains",
    beverages: "Beverages",
    snacks: "Snacks",
    canned: "Canned",
    frozen: "Frozen",
    sauces: "Sauces",
    dairy: "Dairy",
    oils: "Oils",
    
    // Products
    products: "Products",
    addToCart: "Add to Cart",
    addedToCart: "Added to cart!",
    outOfStock: "Out of Stock",
    inStock: "In Stock",
    price: "Price",
    category: "Category",
    searchProducts: "Search products...",
    noProductsFound: "No products found",
    productDetails: "Product Details",
    viewDetails: "View Details",
    
    // Cart
    emptyCart: "Your cart is empty",
    removeFromCart: "Remove",
    continueShopping: "Continue Shopping",
    proceedToCheckout: "Proceed to Checkout",
    cartUpdated: "Cart updated",
    itemRemoved: "Item removed from cart",
    clearCart: "Clear Cart",
    cartCleared: "Cart cleared",
    
    // Orders
    myOrders: "My Orders",
    orderHistory: "Order History",
    orderStatus: "Order Status",
    orderNumber: "Order Number",
    orderDate: "Order Date",
    orderTotal: "Order Total",
    trackOrder: "Track Order",
    reorder: "Reorder",
    noOrders: "No orders yet",
    
    // Order Statuses
    pending: "Pending",
    confirmed: "Confirmed",
    preparing: "Preparing",
    ready: "Ready for Pickup",
    outForDelivery: "Out for Delivery",
    delivered: "Delivered",
    completed: "Completed",
    cancelled: "Cancelled",
    refunded: "Refunded",
    
    // Payment
    paymentMethod: "Payment Method",
    creditCard: "Credit/Debit Card",
    cashOnDelivery: "Cash on Delivery",
    cashOnPickup: "Cash on Pickup",
    giftCardPayment: "Gift Card",
    payNow: "Pay Now",
    paymentSuccess: "Payment successful!",
    paymentFailed: "Payment failed. Please try again.",
    
    // Delivery
    delivery: "Delivery",
    pickup: "Pickup",
    deliveryAddress: "Delivery Address",
    pickupLocation: "Pickup Location",
    deliveryTime: "Delivery Time",
    pickupTime: "Pickup Time",
    estimatedDelivery: "Estimated Delivery",
    freeDelivery: "Free Delivery",
    deliveryFee: "Delivery Fee",
    
    // Admin
    adminPanel: "Admin Panel",
    dashboard: "Dashboard",
    manageProducts: "Manage Products",
    manageOrders: "Manage Orders",
    manageUsers: "Manage Users",
    addProduct: "Add Product",
    editProduct: "Edit Product",
    deleteProduct: "Delete Product",
    productAdded: "Product added successfully!",
    productUpdated: "Product updated successfully!",
    productDeleted: "Product deleted successfully!",
    uploadImage: "Upload Image",
    refineWithAI: "Refine with AI",
    refiningImage: "Refining image with AI...",
    imageRefined: "Image refined successfully!",
    imageUploadFailed: "Failed to upload image",
    
    // Gift Cards
    giftCards: "Gift Cards",
    buyGiftCard: "Buy Gift Card",
    redeemGiftCard: "Redeem Gift Card",
    giftCardAmount: "Gift Card Amount",
    recipientEmail: "Recipient Email",
    recipientName: "Recipient Name",
    senderName: "Your Name",
    personalMessage: "Personal Message",
    giftCardPurchased: "Gift card purchased successfully!",
    giftCardRedeemed: "Gift card redeemed!",
    invalidGiftCard: "Invalid gift card code",
    giftCardBalance: "Gift Card Balance",
    
    // Scanner
    scanBarcode: "Scan Barcode",
    scanToCart: "Scan to Cart",
    scanToEdit: "Scan to Edit",
    scanAnother: "Scan Another",
    productFound: "Product Found!",
    productNotFound: "Product Not Found",
    cameraPermissionDenied: "Camera permission denied",
    cameraError: "Camera error",
    
    // Picture Search
    pictures2cart: "PICTURES2CART",
    picture2edit: "PICTURE2EDIT",
    takePhoto: "Take Photo",
    uploadPhoto: "Upload Photo",
    analyzing: "Analyzing...",
    tryAgain: "Try Again",
    aiSearching: "AI is searching...",
    
    // Messages & Toasts
    success: "Success",
    error: "Error",
    warning: "Warning",
    info: "Info",
    confirmAction: "Are you sure?",
    yes: "Yes",
    no: "No",
    exit: "Exit",
    close: "Close",
    confirm: "Confirm",
    delete: "Delete",
    edit: "Edit",
    view: "View",
    share: "Share",
    copy: "Copy",
    copied: "Copied!",
    saved: "Saved!",
    updated: "Updated!",
    deleted: "Deleted!",
    failed: "Failed",
    tryAgainLater: "Please try again later",
    somethingWentWrong: "Something went wrong",
    networkError: "Network error. Check your connection.",
    
    // Driver
    driverDashboard: "Driver Dashboard",
    availableOrders: "Available Orders",
    myDeliveries: "My Deliveries",
    acceptOrder: "Accept Order",
    startDelivery: "Start Delivery",
    completeDelivery: "Complete Delivery",
    orderAccepted: "Order accepted!",
    deliveryStarted: "Delivery started!",
    deliveryCompleted: "Delivery completed!",
    
    // Notifications
    notifications: "Notifications",
    noNotifications: "No notifications",
    markAsRead: "Mark as read",
    clearAll: "Clear all",
    newOrder: "New Order",
    orderUpdate: "Order Update",
    promotion: "Promotion",
    
    // Store Info
    storeHours: "Store Hours",
    storeLocation: "Store Location",
    contactUs: "Contact Us",
    aboutUs: "About Us",
    
    // Misc
    seeAll: "See All",
    showMore: "Show More",
    showLess: "Show Less",
    filter: "Filter",
    sort: "Sort",
    sortBy: "Sort By",
    priceLowestFirst: "Price: Low to High",
    priceHighestFirst: "Price: High to Low",
    newest: "Newest",
    popular: "Popular",
    inStoreOnly: "In-Store Only",
    onlineOnly: "Online Only",
  },
  
  fr: {
    // Common
    welcome: "Bienvenue",
    signIn: "Se Connecter",
    signUp: "S'inscrire",
    signOut: "Se Déconnecter",
    email: "E-mail",
    password: "Mot de passe",
    confirmPassword: "Confirmer le mot de passe",
    name: "Nom",
    phone: "Téléphone",
    save: "Enregistrer",
    cancel: "Annuler",
    back: "Retour",
    next: "Suivant",
    loading: "Chargement...",
    search: "Rechercher",
    cart: "Panier",
    checkout: "Paiement",
    total: "Total",
    subtotal: "Sous-total",
    tax: "Taxe",
    quantity: "Quantité",
    item: "Article",
    items: "Articles",
    home: "Accueil",
    shop: "Boutique",
    account: "Compte",
    settings: "Paramètres",
    help: "Aide",
    about: "À propos",
    contact: "Contact",
    
    // Auth
    createAccount: "Créer un Compte",
    forgotPassword: "Mot de passe oublié ?",
    resetPassword: "Réinitialiser le mot de passe",
    sendResetCode: "Envoyer le Code",
    enterEmail: "Entrez votre e-mail",
    enterPassword: "Entrez votre mot de passe",
    alreadyHaveAccount: "Vous avez déjà un compte ?",
    dontHaveAccount: "Vous n'avez pas de compte ?",
    passwordResetSent: "Code de réinitialisation envoyé à votre e-mail",
    loginSuccess: "Bon retour !",
    logoutSuccess: "Vous avez été déconnecté",
    accountCreated: "Compte créé avec succès !",
    invalidCredentials: "E-mail ou mot de passe invalide",
    
    // Language
    selectLanguage: "Choisir la Langue",
    language: "Langue",
    
    // Categories
    allCategories: "Toutes les Catégories",
    all: "Tout",
    produce: "Fruits et Légumes",
    meats: "Viandes",
    seafood: "Fruits de Mer",
    spices: "Épices",
    grains: "Céréales",
    beverages: "Boissons",
    snacks: "Collations",
    canned: "Conserves",
    frozen: "Surgelés",
    sauces: "Sauces",
    dairy: "Produits Laitiers",
    oils: "Huiles",
    
    // Products
    products: "Produits",
    addToCart: "Ajouter au Panier",
    addedToCart: "Ajouté au panier !",
    outOfStock: "Rupture de Stock",
    inStock: "En Stock",
    price: "Prix",
    category: "Catégorie",
    searchProducts: "Rechercher des produits...",
    noProductsFound: "Aucun produit trouvé",
    productDetails: "Détails du Produit",
    viewDetails: "Voir les Détails",
    
    // Cart
    emptyCart: "Votre panier est vide",
    removeFromCart: "Retirer",
    continueShopping: "Continuer les Achats",
    proceedToCheckout: "Procéder au Paiement",
    cartUpdated: "Panier mis à jour",
    itemRemoved: "Article retiré du panier",
    clearCart: "Vider le Panier",
    cartCleared: "Panier vidé",
    
    // Orders
    myOrders: "Mes Commandes",
    orderHistory: "Historique des Commandes",
    orderStatus: "Statut de la Commande",
    orderNumber: "Numéro de Commande",
    orderDate: "Date de Commande",
    orderTotal: "Total de la Commande",
    trackOrder: "Suivre la Commande",
    reorder: "Commander à nouveau",
    noOrders: "Aucune commande",
    
    // Order Statuses
    pending: "En Attente",
    confirmed: "Confirmé",
    preparing: "En Préparation",
    ready: "Prêt pour le Retrait",
    outForDelivery: "En Cours de Livraison",
    delivered: "Livré",
    completed: "Terminé",
    cancelled: "Annulé",
    refunded: "Remboursé",
    
    // Payment
    paymentMethod: "Mode de Paiement",
    creditCard: "Carte de Crédit/Débit",
    cashOnDelivery: "Paiement à la Livraison",
    cashOnPickup: "Paiement au Retrait",
    giftCardPayment: "Carte Cadeau",
    payNow: "Payer Maintenant",
    paymentSuccess: "Paiement réussi !",
    paymentFailed: "Paiement échoué. Veuillez réessayer.",
    
    // Delivery
    delivery: "Livraison",
    pickup: "Retrait",
    deliveryAddress: "Adresse de Livraison",
    pickupLocation: "Lieu de Retrait",
    deliveryTime: "Heure de Livraison",
    pickupTime: "Heure de Retrait",
    estimatedDelivery: "Livraison Estimée",
    freeDelivery: "Livraison Gratuite",
    deliveryFee: "Frais de Livraison",
    
    // Admin
    adminPanel: "Panneau Admin",
    dashboard: "Tableau de Bord",
    manageProducts: "Gérer les Produits",
    manageOrders: "Gérer les Commandes",
    manageUsers: "Gérer les Utilisateurs",
    addProduct: "Ajouter un Produit",
    editProduct: "Modifier le Produit",
    deleteProduct: "Supprimer le Produit",
    productAdded: "Produit ajouté avec succès !",
    productUpdated: "Produit mis à jour !",
    productDeleted: "Produit supprimé !",
    uploadImage: "Télécharger une Image",
    refineWithAI: "Affiner avec l'IA",
    refiningImage: "Affinage de l'image avec l'IA...",
    imageRefined: "Image affinée avec succès !",
    imageUploadFailed: "Échec du téléchargement de l'image",
    
    // Gift Cards
    giftCards: "Cartes Cadeaux",
    buyGiftCard: "Acheter une Carte Cadeau",
    redeemGiftCard: "Utiliser une Carte Cadeau",
    giftCardAmount: "Montant de la Carte Cadeau",
    recipientEmail: "E-mail du Destinataire",
    recipientName: "Nom du Destinataire",
    senderName: "Votre Nom",
    personalMessage: "Message Personnel",
    giftCardPurchased: "Carte cadeau achetée !",
    giftCardRedeemed: "Carte cadeau utilisée !",
    invalidGiftCard: "Code de carte cadeau invalide",
    giftCardBalance: "Solde de Carte Cadeau",
    
    // Scanner
    scanBarcode: "Scanner le Code-barres",
    scanToCart: "Scanner pour Ajouter",
    scanToEdit: "Scanner pour Modifier",
    scanAnother: "Scanner un Autre",
    productFound: "Produit Trouvé !",
    productNotFound: "Produit Non Trouvé",
    cameraPermissionDenied: "Permission caméra refusée",
    cameraError: "Erreur de caméra",
    
    // Picture Search
    pictures2cart: "PHOTOS2PANIER",
    picture2edit: "PHOTO2MODIFIER",
    takePhoto: "Prendre une Photo",
    uploadPhoto: "Télécharger une Photo",
    analyzing: "Analyse...",
    tryAgain: "Réessayer",
    aiSearching: "L'IA recherche...",
    
    // Messages & Toasts
    success: "Succès",
    error: "Erreur",
    warning: "Attention",
    info: "Info",
    confirmAction: "Êtes-vous sûr ?",
    yes: "Oui",
    no: "Non",
    exit: "Quitter",
    close: "Fermer",
    confirm: "Confirmer",
    delete: "Supprimer",
    edit: "Modifier",
    view: "Voir",
    share: "Partager",
    copy: "Copier",
    copied: "Copié !",
    saved: "Enregistré !",
    updated: "Mis à jour !",
    deleted: "Supprimé !",
    failed: "Échoué",
    tryAgainLater: "Veuillez réessayer plus tard",
    somethingWentWrong: "Une erreur s'est produite",
    networkError: "Erreur réseau. Vérifiez votre connexion.",
    
    // Driver
    driverDashboard: "Tableau de Bord Chauffeur",
    availableOrders: "Commandes Disponibles",
    myDeliveries: "Mes Livraisons",
    acceptOrder: "Accepter la Commande",
    startDelivery: "Démarrer la Livraison",
    completeDelivery: "Terminer la Livraison",
    orderAccepted: "Commande acceptée !",
    deliveryStarted: "Livraison démarrée !",
    deliveryCompleted: "Livraison terminée !",
    
    // Notifications
    notifications: "Notifications",
    noNotifications: "Aucune notification",
    markAsRead: "Marquer comme lu",
    clearAll: "Tout effacer",
    newOrder: "Nouvelle Commande",
    orderUpdate: "Mise à jour de Commande",
    promotion: "Promotion",
    
    // Store Info
    storeHours: "Heures d'Ouverture",
    storeLocation: "Emplacement du Magasin",
    contactUs: "Contactez-nous",
    aboutUs: "À Propos de Nous",
    
    // Misc
    seeAll: "Voir Tout",
    showMore: "Afficher Plus",
    showLess: "Afficher Moins",
    filter: "Filtrer",
    sort: "Trier",
    sortBy: "Trier Par",
    priceLowestFirst: "Prix: Bas à Élevé",
    priceHighestFirst: "Prix: Élevé à Bas",
    newest: "Plus Récent",
    popular: "Populaire",
    inStoreOnly: "En Magasin Uniquement",
    onlineOnly: "En Ligne Uniquement",
  },
  
  es: {
    // Common
    welcome: "Bienvenido",
    signIn: "Iniciar Sesión",
    signUp: "Registrarse",
    signOut: "Cerrar Sesión",
    email: "Correo",
    password: "Contraseña",
    confirmPassword: "Confirmar Contraseña",
    name: "Nombre",
    phone: "Teléfono",
    save: "Guardar",
    cancel: "Cancelar",
    back: "Atrás",
    next: "Siguiente",
    loading: "Cargando...",
    search: "Buscar",
    cart: "Carrito",
    checkout: "Pagar",
    total: "Total",
    subtotal: "Subtotal",
    tax: "Impuesto",
    quantity: "Cantidad",
    item: "Artículo",
    items: "Artículos",
    home: "Inicio",
    shop: "Tienda",
    account: "Cuenta",
    settings: "Configuración",
    help: "Ayuda",
    about: "Acerca de",
    contact: "Contacto",
    
    // Auth
    createAccount: "Crear Cuenta",
    forgotPassword: "¿Olvidaste tu Contraseña?",
    resetPassword: "Restablecer Contraseña",
    sendResetCode: "Enviar Código",
    enterEmail: "Ingresa tu correo",
    enterPassword: "Ingresa tu contraseña",
    alreadyHaveAccount: "¿Ya tienes una cuenta?",
    dontHaveAccount: "¿No tienes una cuenta?",
    passwordResetSent: "Código de restablecimiento enviado",
    loginSuccess: "¡Bienvenido de vuelta!",
    logoutSuccess: "Has cerrado sesión",
    accountCreated: "¡Cuenta creada con éxito!",
    invalidCredentials: "Correo o contraseña inválidos",
    
    // Language
    selectLanguage: "Seleccionar Idioma",
    language: "Idioma",
    
    // Categories
    allCategories: "Todas las Categorías",
    all: "Todo",
    produce: "Frutas y Verduras",
    meats: "Carnes",
    seafood: "Mariscos",
    spices: "Especias",
    grains: "Granos",
    beverages: "Bebidas",
    snacks: "Botanas",
    canned: "Enlatados",
    frozen: "Congelados",
    sauces: "Salsas",
    dairy: "Lácteos",
    oils: "Aceites",
    
    // Products
    products: "Productos",
    addToCart: "Agregar al Carrito",
    addedToCart: "¡Agregado al carrito!",
    outOfStock: "Agotado",
    inStock: "En Stock",
    price: "Precio",
    category: "Categoría",
    searchProducts: "Buscar productos...",
    noProductsFound: "No se encontraron productos",
    productDetails: "Detalles del Producto",
    viewDetails: "Ver Detalles",
    
    // Cart
    emptyCart: "Tu carrito está vacío",
    removeFromCart: "Eliminar",
    continueShopping: "Continuar Comprando",
    proceedToCheckout: "Proceder al Pago",
    cartUpdated: "Carrito actualizado",
    itemRemoved: "Artículo eliminado del carrito",
    clearCart: "Vaciar Carrito",
    cartCleared: "Carrito vaciado",
    
    // Orders
    myOrders: "Mis Pedidos",
    orderHistory: "Historial de Pedidos",
    orderStatus: "Estado del Pedido",
    orderNumber: "Número de Pedido",
    orderDate: "Fecha del Pedido",
    orderTotal: "Total del Pedido",
    trackOrder: "Rastrear Pedido",
    reorder: "Volver a Pedir",
    noOrders: "Sin pedidos aún",
    
    // Order Statuses
    pending: "Pendiente",
    confirmed: "Confirmado",
    preparing: "Preparando",
    ready: "Listo para Recoger",
    outForDelivery: "En Camino",
    delivered: "Entregado",
    completed: "Completado",
    cancelled: "Cancelado",
    refunded: "Reembolsado",
    
    // Payment
    paymentMethod: "Método de Pago",
    creditCard: "Tarjeta de Crédito/Débito",
    cashOnDelivery: "Pago Contra Entrega",
    cashOnPickup: "Pago al Recoger",
    giftCardPayment: "Tarjeta de Regalo",
    payNow: "Pagar Ahora",
    paymentSuccess: "¡Pago exitoso!",
    paymentFailed: "Pago fallido. Intenta de nuevo.",
    
    // Delivery
    delivery: "Entrega",
    pickup: "Recoger",
    deliveryAddress: "Dirección de Entrega",
    pickupLocation: "Lugar de Recogida",
    deliveryTime: "Hora de Entrega",
    pickupTime: "Hora de Recogida",
    estimatedDelivery: "Entrega Estimada",
    freeDelivery: "Envío Gratis",
    deliveryFee: "Costo de Envío",
    
    // Admin
    adminPanel: "Panel de Admin",
    dashboard: "Panel",
    manageProducts: "Gestionar Productos",
    manageOrders: "Gestionar Pedidos",
    manageUsers: "Gestionar Usuarios",
    addProduct: "Agregar Producto",
    editProduct: "Editar Producto",
    deleteProduct: "Eliminar Producto",
    productAdded: "¡Producto agregado!",
    productUpdated: "¡Producto actualizado!",
    productDeleted: "¡Producto eliminado!",
    uploadImage: "Subir Imagen",
    refineWithAI: "Refinar con IA",
    refiningImage: "Refinando imagen con IA...",
    imageRefined: "¡Imagen refinada!",
    imageUploadFailed: "Error al subir imagen",
    
    // Gift Cards
    giftCards: "Tarjetas de Regalo",
    buyGiftCard: "Comprar Tarjeta",
    redeemGiftCard: "Canjear Tarjeta",
    giftCardAmount: "Monto de la Tarjeta",
    recipientEmail: "Correo del Destinatario",
    recipientName: "Nombre del Destinatario",
    senderName: "Tu Nombre",
    personalMessage: "Mensaje Personal",
    giftCardPurchased: "¡Tarjeta comprada!",
    giftCardRedeemed: "¡Tarjeta canjeada!",
    invalidGiftCard: "Código de tarjeta inválido",
    giftCardBalance: "Saldo de Tarjeta",
    
    // Scanner
    scanBarcode: "Escanear Código",
    scanToCart: "Escanear para Agregar",
    scanToEdit: "Escanear para Editar",
    scanAnother: "Escanear Otro",
    productFound: "¡Producto Encontrado!",
    productNotFound: "Producto No Encontrado",
    cameraPermissionDenied: "Permiso de cámara denegado",
    cameraError: "Error de cámara",
    
    // Picture Search
    pictures2cart: "FOTOS2CARRITO",
    picture2edit: "FOTO2EDITAR",
    takePhoto: "Tomar Foto",
    uploadPhoto: "Subir Foto",
    analyzing: "Analizando...",
    tryAgain: "Intentar de Nuevo",
    aiSearching: "IA buscando...",
    
    // Messages & Toasts
    success: "Éxito",
    error: "Error",
    warning: "Advertencia",
    info: "Info",
    confirmAction: "¿Estás seguro?",
    yes: "Sí",
    no: "No",
    exit: "Salir",
    close: "Cerrar",
    confirm: "Confirmar",
    delete: "Eliminar",
    edit: "Editar",
    view: "Ver",
    share: "Compartir",
    copy: "Copiar",
    copied: "¡Copiado!",
    saved: "¡Guardado!",
    updated: "¡Actualizado!",
    deleted: "¡Eliminado!",
    failed: "Fallido",
    tryAgainLater: "Intenta más tarde",
    somethingWentWrong: "Algo salió mal",
    networkError: "Error de red. Verifica tu conexión.",
    
    // Driver
    driverDashboard: "Panel del Conductor",
    availableOrders: "Pedidos Disponibles",
    myDeliveries: "Mis Entregas",
    acceptOrder: "Aceptar Pedido",
    startDelivery: "Iniciar Entrega",
    completeDelivery: "Completar Entrega",
    orderAccepted: "¡Pedido aceptado!",
    deliveryStarted: "¡Entrega iniciada!",
    deliveryCompleted: "¡Entrega completada!",
    
    // Notifications
    notifications: "Notificaciones",
    noNotifications: "Sin notificaciones",
    markAsRead: "Marcar como leído",
    clearAll: "Limpiar todo",
    newOrder: "Nuevo Pedido",
    orderUpdate: "Actualización de Pedido",
    promotion: "Promoción",
    
    // Store Info
    storeHours: "Horario de la Tienda",
    storeLocation: "Ubicación de la Tienda",
    contactUs: "Contáctanos",
    aboutUs: "Sobre Nosotros",
    
    // Misc
    seeAll: "Ver Todo",
    showMore: "Mostrar Más",
    showLess: "Mostrar Menos",
    filter: "Filtrar",
    sort: "Ordenar",
    sortBy: "Ordenar Por",
    priceLowestFirst: "Precio: Menor a Mayor",
    priceHighestFirst: "Precio: Mayor a Menor",
    newest: "Más Nuevo",
    popular: "Popular",
    inStoreOnly: "Solo en Tienda",
    onlineOnly: "Solo en Línea",
  },
  
  ht: {
    // Haitian Creole
    welcome: "Byenveni",
    signIn: "Konekte",
    signUp: "Enskri",
    signOut: "Dekonekte",
    email: "Imèl",
    password: "Modpas",
    confirmPassword: "Konfime Modpas",
    name: "Non",
    phone: "Telefòn",
    save: "Anrejistre",
    cancel: "Anile",
    back: "Retounen",
    next: "Pwochen",
    loading: "Chajman...",
    search: "Rechèch",
    cart: "Panye",
    checkout: "Peye",
    total: "Total",
    subtotal: "Sou-total",
    tax: "Taks",
    quantity: "Kantite",
    item: "Atik",
    items: "Atik yo",
    home: "Akèy",
    shop: "Magazen",
    account: "Kont",
    settings: "Paramèt",
    help: "Èd",
    about: "Konsènan",
    contact: "Kontak",
    
    createAccount: "Kreye Kont",
    forgotPassword: "Bliye Modpas?",
    resetPassword: "Reyinisyalize Modpas",
    sendResetCode: "Voye Kòd",
    enterEmail: "Antre imèl ou",
    enterPassword: "Antre modpas ou",
    alreadyHaveAccount: "Ou gen yon kont deja?",
    dontHaveAccount: "Ou pa gen yon kont?",
    passwordResetSent: "Kòd reyinisyalizasyon voye",
    loginSuccess: "Byenveni ankò!",
    logoutSuccess: "Ou dekonekte",
    accountCreated: "Kont kreye avèk siksè!",
    invalidCredentials: "Imèl oswa modpas pa bon",
    
    selectLanguage: "Chwazi Lang",
    language: "Lang",
    
    // Categories
    allCategories: "Tout Kategori",
    all: "Tout",
    produce: "Fwi ak Legim",
    meats: "Vyann",
    seafood: "Fwi de mè",
    spices: "Epis",
    grains: "Grenn",
    beverages: "Bwason",
    snacks: "Ti manje",
    canned: "Konsèv",
    frozen: "Frize",
    sauces: "Sos",
    dairy: "Pwodui lèt",
    oils: "Lwil",
    
    products: "Pwodui",
    addToCart: "Ajoute nan Panye",
    addedToCart: "Ajoute nan panye!",
    outOfStock: "Pa Disponib",
    inStock: "Disponib",
    price: "Pri",
    category: "Kategori",
    searchProducts: "Chèche pwodui...",
    noProductsFound: "Pa jwenn pwodui",
    productDetails: "Detay Pwodui",
    viewDetails: "Wè Detay",
    
    emptyCart: "Panye ou vid",
    removeFromCart: "Retire",
    continueShopping: "Kontinye Achte",
    proceedToCheckout: "Ale nan Peyman",
    cartUpdated: "Panye mete ajou",
    itemRemoved: "Atik retire nan panye",
    clearCart: "Vide Panye",
    cartCleared: "Panye vid",
    
    myOrders: "Kòmand Mwen",
    orderHistory: "Istwa Kòmand",
    orderStatus: "Estati Kòmand",
    orderNumber: "Nimewo Kòmand",
    orderDate: "Dat Kòmand",
    orderTotal: "Total Kòmand",
    trackOrder: "Swiv Kòmand",
    reorder: "Kòmande ankò",
    noOrders: "Pa gen kòmand",
    
    pending: "Ap Tann",
    confirmed: "Konfime",
    preparing: "Ap Prepare",
    ready: "Pare pou Pran",
    outForDelivery: "An Wout",
    delivered: "Livre",
    completed: "Fini",
    cancelled: "Anile",
    refunded: "Ranbouse",
    
    paymentMethod: "Metòd Peman",
    creditCard: "Kat Kredi/Debi",
    cashOnDelivery: "Peye lè Livrezon",
    cashOnPickup: "Peye lè Pran",
    giftCardPayment: "Kat Kado",
    payNow: "Peye Kounye a",
    paymentSuccess: "Peman reyisi!",
    paymentFailed: "Peman echwe. Eseye ankò.",
    
    delivery: "Livrezon",
    pickup: "Pran",
    deliveryAddress: "Adrès Livrezon",
    pickupLocation: "Kote pou Pran",
    deliveryTime: "Lè Livrezon",
    pickupTime: "Lè pou Pran",
    estimatedDelivery: "Livrezon Estime",
    freeDelivery: "Livrezon Gratis",
    deliveryFee: "Frè Livrezon",
    
    adminPanel: "Panno Admin",
    dashboard: "Tablo",
    manageProducts: "Jere Pwodui",
    manageOrders: "Jere Kòmand",
    manageUsers: "Jere Itilizatè",
    addProduct: "Ajoute Pwodui",
    editProduct: "Modifye Pwodui",
    deleteProduct: "Efase Pwodui",
    productAdded: "Pwodui ajoute!",
    productUpdated: "Pwodui mete ajou!",
    productDeleted: "Pwodui efase!",
    uploadImage: "Telechaje Imaj",
    refineWithAI: "Rafine ak AI",
    refiningImage: "Ap rafine imaj ak AI...",
    imageRefined: "Imaj rafine avèk siksè!",
    imageUploadFailed: "Echwe telechaje imaj",
    
    giftCards: "Kat Kado",
    buyGiftCard: "Achte Kat Kado",
    redeemGiftCard: "Itilize Kat Kado",
    giftCardAmount: "Montan Kat Kado",
    recipientEmail: "Imèl Destinatè",
    recipientName: "Non Destinatè",
    senderName: "Non Ou",
    personalMessage: "Mesaj Pèsonèl",
    giftCardPurchased: "Kat kado achte!",
    giftCardRedeemed: "Kat kado itilize!",
    invalidGiftCard: "Kòd kat kado pa bon",
    giftCardBalance: "Balans Kat Kado",
    
    scanBarcode: "Eskane Kòd ba",
    scanToCart: "Eskane pou Ajoute",
    scanToEdit: "Eskane pou Modifye",
    scanAnother: "Eskane Yon Lòt",
    productFound: "Pwodui Jwenn!",
    productNotFound: "Pwodui Pa Jwenn",
    cameraPermissionDenied: "Pèmisyon kamera refize",
    cameraError: "Erè kamera",
    
    pictures2cart: "FOTO2PANYE",
    picture2edit: "FOTO2MODIFYE",
    takePhoto: "Pran Foto",
    uploadPhoto: "Telechaje Foto",
    analyzing: "Ap analize...",
    tryAgain: "Eseye Ankò",
    aiSearching: "AI ap chèche...",
    
    success: "Siksè",
    error: "Erè",
    warning: "Avètisman",
    info: "Info",
    confirmAction: "Ou sèten?",
    yes: "Wi",
    no: "Non",
    exit: "Sòti",
    close: "Fèmen",
    confirm: "Konfime",
    delete: "Efase",
    edit: "Modifye",
    view: "Gade",
    share: "Pataje",
    copy: "Kopye",
    copied: "Kopye!",
    saved: "Anrejistre!",
    updated: "Mete ajou!",
    deleted: "Efase!",
    failed: "Echwe",
    tryAgainLater: "Eseye pita",
    somethingWentWrong: "Gen yon pwoblèm",
    networkError: "Erè rezo. Tcheke koneksyon ou.",
    
    driverDashboard: "Tablo Chofè",
    availableOrders: "Kòmand Disponib",
    myDeliveries: "Livrezon Mwen",
    acceptOrder: "Aksepte Kòmand",
    startDelivery: "Kòmanse Livrezon",
    completeDelivery: "Fini Livrezon",
    orderAccepted: "Kòmand aksepte!",
    deliveryStarted: "Livrezon kòmanse!",
    deliveryCompleted: "Livrezon fini!",
    
    notifications: "Notifikasyon",
    noNotifications: "Pa gen notifikasyon",
    markAsRead: "Make kòm li",
    clearAll: "Efase tout",
    newOrder: "Nouvo Kòmand",
    orderUpdate: "Mizajou Kòmand",
    promotion: "Pwomosyon",
    
    storeHours: "Lè Magazen",
    storeLocation: "Kote Magazen",
    contactUs: "Kontakte Nou",
    aboutUs: "Konsènan Nou",
    
    seeAll: "Wè Tout",
    showMore: "Montre Plis",
    showLess: "Montre Mwens",
    filter: "Filtre",
    sort: "Triye",
    sortBy: "Triye Pa",
    priceLowestFirst: "Pri: Ba a Wo",
    priceHighestFirst: "Pri: Wo a Ba",
    newest: "Pi Nouvo",
    popular: "Popilè",
    inStoreOnly: "Nan Magazen Sèlman",
    onlineOnly: "Anliy Sèlman",
  },
  
  ha: {
    // Hausa - Nigerian language
    welcome: "Barka da zuwa",
    signIn: "Shiga",
    signUp: "Yi Rijista",
    signOut: "Fita",
    email: "Imel",
    password: "Kalmar sirri",
    confirmPassword: "Tabbatar da Kalmar sirri",
    name: "Suna",
    phone: "Waya",
    save: "Ajiye",
    cancel: "Soke",
    back: "Koma",
    next: "Gaba",
    loading: "Ana lodi...",
    search: "Bincika",
    cart: "Kwandon",
    checkout: "Biya",
    total: "Jimlar",
    subtotal: "Ƙaramin jimla",
    tax: "Haraji",
    quantity: "Yawa",
    item: "Kaya",
    items: "Kayayyaki",
    home: "Gida",
    shop: "Shagon",
    account: "Asusu",
    settings: "Saituna",
    help: "Taimako",
    about: "Game da",
    contact: "Tuntuɓa",
    
    createAccount: "Ƙirƙiri Asusu",
    forgotPassword: "Manta Kalmar sirri?",
    resetPassword: "Sake Kalmar sirri",
    sendResetCode: "Aika Lambar",
    enterEmail: "Shigar da imel ɗinka",
    enterPassword: "Shigar da kalmar sirri",
    alreadyHaveAccount: "Kana da asusu?",
    dontHaveAccount: "Ba ka da asusu?",
    passwordResetSent: "An aika lambar sake saita",
    loginSuccess: "Barka da dawowar!",
    logoutSuccess: "Ka fita",
    accountCreated: "An ƙirƙiri asusu!",
    invalidCredentials: "Imel ko kalmar sirri ba daidai ba",
    
    selectLanguage: "Zaɓi Harshe",
    language: "Harshe",
    
    allCategories: "Duk Nau'o'i",
    all: "Duka",
    produce: "Kayan lambu",
    meats: "Nama",
    seafood: "Kifin Ruwa",
    spices: "Yaji",
    grains: "Hatsi",
    beverages: "Abin sha",
    snacks: "Cunkus",
    canned: "Gwangwani",
    frozen: "Daskararre",
    sauces: "Miya",
    dairy: "Madara",
    oils: "Mai",
    
    products: "Kayayyaki",
    addToCart: "Ƙara zuwa Kwandon",
    addedToCart: "An ƙara zuwa kwandon!",
    outOfStock: "Ba a samu ba",
    inStock: "Akwai",
    price: "Farashin",
    category: "Nau'i",
    searchProducts: "Bincika kayayyaki...",
    noProductsFound: "Ba a samu kayayyaki ba",
    productDetails: "Bayanan Kaya",
    viewDetails: "Duba Bayanai",
    
    emptyCart: "Kwandon ka babu komai",
    removeFromCart: "Cire",
    continueShopping: "Ci gaba da Siyayya",
    proceedToCheckout: "Je Biya",
    cartUpdated: "An sabunta kwandon",
    itemRemoved: "An cire kaya daga kwandon",
    clearCart: "Share Kwandon",
    cartCleared: "An share kwandon",
    
    myOrders: "Umarni na",
    orderHistory: "Tarihin Umarni",
    orderStatus: "Matsayin Umarni",
    orderNumber: "Lambar Umarni",
    orderDate: "Ranar Umarni",
    orderTotal: "Jimlar Umarni",
    trackOrder: "Bi Umarni",
    reorder: "Sake Umarni",
    noOrders: "Babu umarni",
    
    pending: "Ana jira",
    confirmed: "An tabbatar",
    preparing: "Ana shirya",
    ready: "A shirye don ɗauka",
    outForDelivery: "A hanya",
    delivered: "An kawo",
    completed: "An gama",
    cancelled: "An soke",
    refunded: "An mayar",
    
    paymentMethod: "Hanyar Biya",
    creditCard: "Katin Bashi",
    cashOnDelivery: "Biya a Isar",
    cashOnPickup: "Biya a Ɗauka",
    giftCardPayment: "Katin Kyauta",
    payNow: "Biya Yanzu",
    paymentSuccess: "Biya ya yi nasara!",
    paymentFailed: "Biya bai yi ba. Sake gwadawa.",
    
    delivery: "Isar",
    pickup: "Ɗauka",
    deliveryAddress: "Adireshin Isar",
    pickupLocation: "Wurin Ɗauka",
    deliveryTime: "Lokacin Isar",
    pickupTime: "Lokacin Ɗauka",
    estimatedDelivery: "Isar da aka kiyasta",
    freeDelivery: "Isar Kyauta",
    deliveryFee: "Kuɗin Isar",
    
    adminPanel: "Kwamitin Admin",
    dashboard: "Allon Mulki",
    manageProducts: "Sarrafa Kayayyaki",
    manageOrders: "Sarrafa Umarni",
    manageUsers: "Sarrafa Masu amfani",
    addProduct: "Ƙara Kaya",
    editProduct: "Gyara Kaya",
    deleteProduct: "Share Kaya",
    productAdded: "An ƙara kaya!",
    productUpdated: "An sabunta kaya!",
    productDeleted: "An share kaya!",
    uploadImage: "Ɗora Hoto",
    refineWithAI: "Gyara da AI",
    refiningImage: "Ana gyara hoto da AI...",
    imageRefined: "An gyara hoto!",
    imageUploadFailed: "Ba a iya ɗora hoto",
    
    giftCards: "Katin Kyauta",
    buyGiftCard: "Saya Katin Kyauta",
    redeemGiftCard: "Yi amfani da Katin",
    giftCardAmount: "Adadin Katin",
    recipientEmail: "Imel Mai karɓa",
    recipientName: "Sunan Mai karɓa",
    senderName: "Sunanka",
    personalMessage: "Saƙo na Mutum",
    giftCardPurchased: "An saya katin kyauta!",
    giftCardRedeemed: "An yi amfani da katin!",
    invalidGiftCard: "Lambar katin ba daidai ba",
    giftCardBalance: "Ragowar Katin",
    
    scanBarcode: "Duba Lambar sandarwa",
    scanToCart: "Duba don Ƙarawa",
    scanToEdit: "Duba don Gyarawa",
    scanAnother: "Duba Wani",
    productFound: "An samu Kaya!",
    productNotFound: "Ba a samu Kaya ba",
    cameraPermissionDenied: "An hana amfani da kyamara",
    cameraError: "Kuskuren kyamara",
    
    pictures2cart: "HOTO2KWANDON",
    picture2edit: "HOTO2GYARA",
    takePhoto: "Ɗauki Hoto",
    uploadPhoto: "Ɗora Hoto",
    analyzing: "Ana nazari...",
    tryAgain: "Sake Gwadawa",
    aiSearching: "AI na nema...",
    
    success: "Nasara",
    error: "Kuskure",
    warning: "Gargaɗi",
    info: "Bayani",
    confirmAction: "Ka tabbata?",
    yes: "Eh",
    no: "A'a",
    exit: "Fita",
    close: "Rufe",
    confirm: "Tabbatar",
    delete: "Share",
    edit: "Gyara",
    view: "Duba",
    share: "Raba",
    copy: "Kwafa",
    copied: "An kwafa!",
    saved: "An ajiye!",
    updated: "An sabunta!",
    deleted: "An share!",
    failed: "Bai yi ba",
    tryAgainLater: "Gwada daga baya",
    somethingWentWrong: "Wani abu ya faru",
    networkError: "Kuskuren hanyar sadarwa.",
    
    driverDashboard: "Allon Direba",
    availableOrders: "Umarni da ke akwai",
    myDeliveries: "Isarwa na",
    acceptOrder: "Karɓi Umarni",
    startDelivery: "Fara Isarwa",
    completeDelivery: "Gama Isarwa",
    orderAccepted: "An karɓi umarni!",
    deliveryStarted: "Isarwa ta fara!",
    deliveryCompleted: "Isarwa ta gama!",
    
    notifications: "Sanarwa",
    noNotifications: "Babu sanarwa",
    markAsRead: "Nuna an karanta",
    clearAll: "Share duka",
    newOrder: "Sabon Umarni",
    orderUpdate: "Sabuntawar Umarni",
    promotion: "Tallafin",
    
    storeHours: "Awannin Shagon",
    storeLocation: "Wurin Shagon",
    contactUs: "Tuntuɓe mu",
    aboutUs: "Game da mu",
    
    seeAll: "Duba Duka",
    showMore: "Nuna Ƙari",
    showLess: "Nuna Ƙasa",
    filter: "Tace",
    sort: "Tsara",
    sortBy: "Tsara ta",
    priceLowestFirst: "Farashi: Ƙasa zuwa Sama",
    priceHighestFirst: "Farashi: Sama zuwa Ƙasa",
    newest: "Sabbin",
    popular: "Mashahuri",
    inStoreOnly: "A Shagon Kawai",
    onlineOnly: "Akan Layi Kawai",
  },
  
  yo: {
    // Yoruba - Nigerian language
    welcome: "Ẹ káàbọ̀",
    signIn: "Wọlé",
    signUp: "Forúkọsílẹ̀",
    signOut: "Jáde",
    email: "Ímeèlì",
    password: "Ọ̀rọ̀ aṣínà",
    confirmPassword: "Jẹ́rìísí Ọ̀rọ̀ aṣínà",
    name: "Orúkọ",
    phone: "Fóònù",
    save: "Fi pamọ́",
    cancel: "Fagilé",
    back: "Padà",
    next: "Tẹ̀lé",
    loading: "Ń gbéru...",
    search: "Ṣàwárí",
    cart: "Agbọ̀n",
    checkout: "Sanwó",
    total: "Àpapọ̀",
    subtotal: "Ìṣirò kékeré",
    tax: "Owó orí",
    quantity: "Iye",
    item: "Nǹkan",
    items: "Àwọn nǹkan",
    home: "Ilé",
    shop: "Ṣọ́ọ̀bù",
    account: "Àkọọ́lẹ̀",
    settings: "Ètò",
    help: "Ìrànlọ́wọ́",
    about: "Nípa",
    contact: "Kàn sí",
    
    createAccount: "Ṣẹ̀dá Àkọọ́lẹ̀",
    forgotPassword: "Gbàgbé Ọ̀rọ̀ aṣínà?",
    resetPassword: "Tún Ọ̀rọ̀ aṣínà Ṣe",
    sendResetCode: "Fi Kóòdù Ránṣẹ́",
    enterEmail: "Tẹ ímeèlì rẹ",
    enterPassword: "Tẹ ọ̀rọ̀ aṣínà rẹ",
    alreadyHaveAccount: "Ṣé o ní àkọọ́lẹ̀ tẹ́lẹ̀?",
    dontHaveAccount: "Ṣé o kò ní àkọọ́lẹ̀?",
    passwordResetSent: "A ti fi kóòdù àtúnṣe ránṣẹ́",
    loginSuccess: "Ẹ káàbọ̀ padà!",
    logoutSuccess: "O ti jáde",
    accountCreated: "A ti ṣẹ̀dá àkọọ́lẹ̀!",
    invalidCredentials: "Ímeèlì tàbí ọ̀rọ̀ aṣínà kò tọ́",
    
    selectLanguage: "Yan Èdè",
    language: "Èdè",
    
    allCategories: "Gbogbo Ẹ̀ka",
    all: "Gbogbo",
    produce: "Èso àti Ewébẹ̀",
    meats: "Ẹran",
    seafood: "Ẹja Òkun",
    spices: "Atà àti Ọṣẹ",
    grains: "Ọkà",
    beverages: "Ohun Mímu",
    snacks: "Oúnjẹ fífẹ́",
    canned: "Oúnjẹ tín",
    frozen: "Dídì",
    sauces: "Ọbẹ̀",
    dairy: "Wàrà",
    oils: "Epo",
    
    products: "Àwọn Ọjà",
    addToCart: "Fi Sí Agbọ̀n",
    addedToCart: "A ti fi sí agbọ̀n!",
    outOfStock: "Kò sí",
    inStock: "Wà ní ọjà",
    price: "Owó",
    category: "Ẹ̀ka",
    searchProducts: "Ṣàwárí ọjà...",
    noProductsFound: "A kò rí ọjà",
    productDetails: "Àlàyé Ọjà",
    viewDetails: "Wo Àlàyé",
    
    emptyCart: "Agbọ̀n rẹ ṣòfo",
    removeFromCart: "Yọkurò",
    continueShopping: "Tẹ̀síwájú Ṣọ́pìṅ",
    proceedToCheckout: "Lọ Sanwó",
    cartUpdated: "A ti ṣe àtúnṣe agbọ̀n",
    itemRemoved: "A ti yọ nǹkan kúrò",
    clearCart: "Pa Agbọ̀n rẹ́",
    cartCleared: "A ti pa agbọ̀n rẹ́",
    
    myOrders: "Àwọn Àṣẹ Mi",
    orderHistory: "Ìtàn Àṣẹ",
    orderStatus: "Ipò Àṣẹ",
    orderNumber: "Nọ́mbà Àṣẹ",
    orderDate: "Ọjọ́ Àṣẹ",
    orderTotal: "Àpapọ̀ Àṣẹ",
    trackOrder: "Tọpinpin Àṣẹ",
    reorder: "Tún Ṣe Àṣẹ",
    noOrders: "Kò sí àṣẹ",
    
    pending: "Ń dúró",
    confirmed: "Ti jẹ́rìísí",
    preparing: "Ń múra",
    ready: "Ṣetán fún Gbígbà",
    outForDelivery: "Ní ọ̀nà",
    delivered: "Ti dé",
    completed: "Ti parí",
    cancelled: "Ti fagilé",
    refunded: "Ti san padà",
    
    paymentMethod: "Ọ̀nà Ìsanwó",
    creditCard: "Káàdì Krẹ́dítì",
    cashOnDelivery: "Sanwó nígbà Ìfibọ̀",
    cashOnPickup: "Sanwó nígbà Gbígbà",
    giftCardPayment: "Káàdì Ẹ̀bùn",
    payNow: "Sanwó Báyìí",
    paymentSuccess: "Ìsanwó ti ṣàṣeyọrí!",
    paymentFailed: "Ìsanwó kò ṣe. Gbìyànjú lẹ́ẹ̀kan sí i.",
    
    delivery: "Ìfibọ̀",
    pickup: "Gbígbà",
    deliveryAddress: "Àdírẹ́sì Ìfibọ̀",
    pickupLocation: "Ibi Gbígbà",
    deliveryTime: "Àkókò Ìfibọ̀",
    pickupTime: "Àkókò Gbígbà",
    estimatedDelivery: "Àkókò Ìfibọ̀ tí a ṣírò",
    freeDelivery: "Ìfibọ̀ Ọ̀fẹ́",
    deliveryFee: "Owó Ìfibọ̀",
    
    adminPanel: "Pánẹ́lì Admin",
    dashboard: "Pẹpẹ",
    manageProducts: "Ṣàkóso Ọjà",
    manageOrders: "Ṣàkóso Àṣẹ",
    manageUsers: "Ṣàkóso Àwọn Olùmúlò",
    addProduct: "Fi Ọjà Kún",
    editProduct: "Ṣàtúnṣe Ọjà",
    deleteProduct: "Pa Ọjà rẹ́",
    productAdded: "A ti fi ọjà kún!",
    productUpdated: "A ti ṣàtúnṣe ọjà!",
    productDeleted: "A ti pa ọjà rẹ́!",
    uploadImage: "Gbéru Àwòrán",
    refineWithAI: "Tún Ṣe pẹ̀lú AI",
    refiningImage: "Ń tún àwòrán ṣe pẹ̀lú AI...",
    imageRefined: "A ti tún àwòrán ṣe!",
    imageUploadFailed: "Kò lè gbéru àwòrán",
    
    giftCards: "Káàdì Ẹ̀bùn",
    buyGiftCard: "Ra Káàdì Ẹ̀bùn",
    redeemGiftCard: "Lo Káàdì Ẹ̀bùn",
    giftCardAmount: "Iye Káàdì",
    recipientEmail: "Ímeèlì Olùgbà",
    recipientName: "Orúkọ Olùgbà",
    senderName: "Orúkọ Rẹ",
    personalMessage: "Ìfiránṣẹ́ Ti ara ẹni",
    giftCardPurchased: "A ti ra káàdì ẹ̀bùn!",
    giftCardRedeemed: "A ti lo káàdì ẹ̀bùn!",
    invalidGiftCard: "Kóòdù káàdì kò tọ́",
    giftCardBalance: "Ìyókù Káàdì",
    
    scanBarcode: "Ṣàyẹ̀wò Kóòdù",
    scanToCart: "Ṣàyẹ̀wò láti Fi kún",
    scanToEdit: "Ṣàyẹ̀wò láti Ṣàtúnṣe",
    scanAnother: "Ṣàyẹ̀wò Mìíràn",
    productFound: "A ti rí Ọjà!",
    productNotFound: "A kò rí Ọjà",
    cameraPermissionDenied: "A kọ àṣẹ kámẹ́rà",
    cameraError: "Àṣìṣe kámẹ́rà",
    
    pictures2cart: "FỌ́TÒ2AGBỌ̀N",
    picture2edit: "FỌ́TÒ2ṢÀTÚNṢE",
    takePhoto: "Ya Fọ́tò",
    uploadPhoto: "Gbéru Fọ́tò",
    analyzing: "Ń ṣàyẹ̀wò...",
    tryAgain: "Gbìyànjú Lẹ́ẹ̀kan sí i",
    aiSearching: "AI ń wá...",
    
    success: "Àṣeyọrí",
    error: "Àṣìṣe",
    warning: "Ìkìlọ̀",
    info: "Àlàyé",
    confirmAction: "Ṣé o dájú?",
    yes: "Bẹ́ẹ̀ni",
    no: "Rárá",
    exit: "Jáde",
    close: "Tì",
    confirm: "Jẹ́rìísí",
    delete: "Pa rẹ́",
    edit: "Ṣàtúnṣe",
    view: "Wo",
    share: "Pín",
    copy: "Dàkọ",
    copied: "A ti dàkọ!",
    saved: "A ti fi pamọ́!",
    updated: "A ti ṣàtúnṣe!",
    deleted: "A ti pa rẹ́!",
    failed: "Kò ṣe",
    tryAgainLater: "Gbìyànjú lẹ́yìn náà",
    somethingWentWrong: "Nǹkankan ti ṣẹlẹ̀",
    networkError: "Àṣìṣe nẹ́tíwọ́ọ̀kì.",
    
    driverDashboard: "Pẹpẹ Awakọ̀",
    availableOrders: "Àwọn Àṣẹ Tó Wà",
    myDeliveries: "Àwọn Ìfibọ̀ Mi",
    acceptOrder: "Gba Àṣẹ",
    startDelivery: "Bẹ̀rẹ̀ Ìfibọ̀",
    completeDelivery: "Parí Ìfibọ̀",
    orderAccepted: "A ti gba àṣẹ!",
    deliveryStarted: "Ìfibọ̀ ti bẹ̀rẹ̀!",
    deliveryCompleted: "Ìfibọ̀ ti parí!",
    
    notifications: "Ìfitónilétí",
    noNotifications: "Kò sí ìfitónilétí",
    markAsRead: "Ṣàmì bí a ti kà",
    clearAll: "Pa gbogbo rẹ́",
    newOrder: "Àṣẹ Tuntun",
    orderUpdate: "Àtúnṣe Àṣẹ",
    promotion: "Ìpolówó",
    
    storeHours: "Wákàtí Ṣọ́ọ̀bù",
    storeLocation: "Ibi Ṣọ́ọ̀bù",
    contactUs: "Kàn sí Wa",
    aboutUs: "Nípa Wa",
    
    seeAll: "Wo Gbogbo",
    showMore: "Fi Síwájú hàn",
    showLess: "Fi Díẹ̀ hàn",
    filter: "Ṣà",
    sort: "Tò",
    sortBy: "Tò Nípasẹ̀",
    priceLowestFirst: "Owó: Kékeré sí Ńlá",
    priceHighestFirst: "Owó: Ńlá sí Kékeré",
    newest: "Tuntun jùlọ",
    popular: "Olókìkí",
    inStoreOnly: "Nínú Ṣọ́ọ̀bù Nìkan",
    onlineOnly: "Lórí Íntánẹ́ẹ̀tì Nìkan",
  },
  
  ig: {
    // Igbo - Nigerian language
    welcome: "Nnọọ",
    signIn: "Banye",
    signUp: "Debanye aha",
    signOut: "Pụọ",
    email: "Email",
    password: "Okwuntughe",
    confirmPassword: "Kwado Okwuntughe",
    name: "Aha",
    phone: "Ekwentị",
    save: "Chekwaa",
    cancel: "Kagbuo",
    back: "Laghachi",
    next: "Ọzọ",
    loading: "Na-ebu...",
    search: "Chọọ",
    cart: "Ngwa",
    checkout: "Kwụọ ụgwọ",
    total: "Niile",
    subtotal: "Obere niile",
    tax: "Ụtụ",
    quantity: "Ọnụọgụ",
    item: "Ihe",
    items: "Ihe ndị",
    home: "Ụlọ",
    shop: "Ụlọ ahịa",
    account: "Akaụntụ",
    settings: "Ntọala",
    help: "Enyemaka",
    about: "Maka",
    contact: "Kpọtụrụ",
    
    createAccount: "Mepụta Akaụntụ",
    forgotPassword: "Chefuo Okwuntughe?",
    resetPassword: "Tọgharia Okwuntughe",
    sendResetCode: "Ziga Koodu",
    enterEmail: "Tinye email gị",
    enterPassword: "Tinye okwuntughe gị",
    alreadyHaveAccount: "Ị nwere akaụntụ?",
    dontHaveAccount: "Ị nweghị akaụntụ?",
    passwordResetSent: "E zigara koodu ntọgharia",
    loginSuccess: "Nnọọ ọzọ!",
    logoutSuccess: "Ị pụọla",
    accountCreated: "E mepụtara akaụntụ!",
    invalidCredentials: "Email ma ọ bụ okwuntughe ezighi ezi",
    
    selectLanguage: "Họrọ Asụsụ",
    language: "Asụsụ",
    
    allCategories: "Ụdị Niile",
    all: "Niile",
    produce: "Mkpụrụ osisi na akwụkwọ nri",
    meats: "Anụ",
    seafood: "Azụ",
    spices: "Ọsẹ",
    grains: "Ọka",
    beverages: "Ihe ọṅụṅụ",
    snacks: "Nri nta",
    canned: "Nri tịn",
    frozen: "Nri oyi",
    sauces: "Ofe",
    dairy: "Mmiri ara ehi",
    oils: "Mmanụ",
    
    products: "Ngwa ahịa",
    addToCart: "Tinye na Ngwa",
    addedToCart: "Etinyela na ngwa!",
    outOfStock: "Ọ nweghị",
    inStock: "Ọ dị",
    price: "Ọnụahịa",
    category: "Ụdị",
    searchProducts: "Chọọ ngwa ahịa...",
    noProductsFound: "Ahụghị ngwa ahịa",
    productDetails: "Nkọwa ngwa ahịa",
    viewDetails: "Lee Nkọwa",
    
    emptyCart: "Ngwa gị tọgbọrọ",
    removeFromCart: "Wepu",
    continueShopping: "Gaa n'ihu Ịzụ ahịa",
    proceedToCheckout: "Gaa Kwụọ ụgwọ",
    cartUpdated: "Emelitela ngwa",
    itemRemoved: "E wepụla ihe",
    clearCart: "Kpochaa Ngwa",
    cartCleared: "Akpochaala ngwa",
    
    myOrders: "Iwu m",
    orderHistory: "Akụkọ Iwu",
    orderStatus: "Ọnọdụ Iwu",
    orderNumber: "Nọmba Iwu",
    orderDate: "Ụbọchị Iwu",
    orderTotal: "Mkpokọta Iwu",
    trackOrder: "Soro Iwu",
    reorder: "Nyeghachi iwu",
    noOrders: "Enweghị iwu",
    
    pending: "Na-eche",
    confirmed: "Ekwenyere",
    preparing: "Na-akwado",
    ready: "Dị njikere maka ịnara",
    outForDelivery: "N'ụzọ",
    delivered: "E buteela",
    completed: "Emechara",
    cancelled: "Akagburu",
    refunded: "Eweghachiri ego",
    
    paymentMethod: "Ụzọ Ịkwụ Ụgwọ",
    creditCard: "Kaadị Kredit",
    cashOnDelivery: "Kwụọ ụgwọ n'ịbute",
    cashOnPickup: "Kwụọ ụgwọ n'ịnara",
    giftCardPayment: "Kaadị Onyinye",
    payNow: "Kwụọ ụgwọ Ugbu a",
    paymentSuccess: "Ịkwụ ụgwọ gara nke ọma!",
    paymentFailed: "Ịkwụ ụgwọ adaghị. Nwaa ọzọ.",
    
    delivery: "Ịbute",
    pickup: "Ịnara",
    deliveryAddress: "Adreesị Ịbute",
    pickupLocation: "Ebe Ịnara",
    deliveryTime: "Oge Ịbute",
    pickupTime: "Oge Ịnara",
    estimatedDelivery: "Ịbute Akụkọ",
    freeDelivery: "Ịbute n'efu",
    deliveryFee: "Ụgwọ Ịbute",
    
    adminPanel: "Panel Admin",
    dashboard: "Dashboodu",
    manageProducts: "Jikwaa Ngwa ahịa",
    manageOrders: "Jikwaa Iwu",
    manageUsers: "Jikwaa Ndị ọrụ",
    addProduct: "Tinye Ngwa ahịa",
    editProduct: "Dezie Ngwa ahịa",
    deleteProduct: "Hichapụ Ngwa ahịa",
    productAdded: "Etinyela ngwa ahịa!",
    productUpdated: "Emelitela ngwa ahịa!",
    productDeleted: "Ehichapụla ngwa ahịa!",
    uploadImage: "Bulite Foto",
    refineWithAI: "Mee ka ọ dị mma site na AI",
    refiningImage: "Na-emezi foto site na AI...",
    imageRefined: "E meziri foto!",
    imageUploadFailed: "Enweghị ike ibulite foto",
    
    giftCards: "Kaadị Onyinye",
    buyGiftCard: "Zụta Kaadị Onyinye",
    redeemGiftCard: "Jiri Kaadị Onyinye",
    giftCardAmount: "Ego Kaadị",
    recipientEmail: "Email Onye na-anata",
    recipientName: "Aha Onye na-anata",
    senderName: "Aha Gị",
    personalMessage: "Ozi Nkeonwe",
    giftCardPurchased: "Azụtara kaadị onyinye!",
    giftCardRedeemed: "Ejirila kaadị onyinye!",
    invalidGiftCard: "Koodu kaadị ezighi ezi",
    giftCardBalance: "Ego Fọdụrụ na Kaadị",
    
    scanBarcode: "Nyochaa Koodu",
    scanToCart: "Nyochaa iji Tinye",
    scanToEdit: "Nyochaa iji Dezie",
    scanAnother: "Nyochaa Ọzọ",
    productFound: "Achọtara Ngwa ahịa!",
    productNotFound: "Ahụghị Ngwa ahịa",
    cameraPermissionDenied: "Ajụrụ ikike kamera",
    cameraError: "Njehie kamera",
    
    pictures2cart: "FOTO2NGWA",
    picture2edit: "FOTO2DEZIE",
    takePhoto: "See Foto",
    uploadPhoto: "Bulite Foto",
    analyzing: "Na-enyocha...",
    tryAgain: "Nwaa Ọzọ",
    aiSearching: "AI na-achọ...",
    
    success: "Ọganihu",
    error: "Njehie",
    warning: "Ịdọ aka ná ntị",
    info: "Ozi",
    confirmAction: "Ị ji n'aka?",
    yes: "Ee",
    no: "Mba",
    exit: "Pụọ",
    close: "Mechie",
    confirm: "Kwado",
    delete: "Hichapụ",
    edit: "Dezie",
    view: "Lee",
    share: "Kekọrịta",
    copy: "Detuo",
    copied: "E detuola!",
    saved: "Echekwala!",
    updated: "Emelitela!",
    deleted: "Ehichapụla!",
    failed: "Adaghị",
    tryAgainLater: "Nwaa mgbe e mesịrị",
    somethingWentWrong: "Ihe adaghị",
    networkError: "Njehie netwọk.",
    
    driverDashboard: "Dashboodu Onye ọkwọ",
    availableOrders: "Iwu dị",
    myDeliveries: "Ịbute m",
    acceptOrder: "Nabata Iwu",
    startDelivery: "Malite Ịbute",
    completeDelivery: "Mechaa Ịbute",
    orderAccepted: "Anabatala iwu!",
    deliveryStarted: "Ịbute amalitela!",
    deliveryCompleted: "Ịbute emechala!",
    
    notifications: "Ọkwa",
    noNotifications: "Enweghị ọkwa",
    markAsRead: "Kaa dị ka agụrụ",
    clearAll: "Kpochaa niile",
    newOrder: "Iwu Ọhụrụ",
    orderUpdate: "Mmelite Iwu",
    promotion: "Nkwado",
    
    storeHours: "Oge Ụlọ ahịa",
    storeLocation: "Ebe Ụlọ ahịa",
    contactUs: "Kpọtụrụ Anyị",
    aboutUs: "Maka Anyị",
    
    seeAll: "Lee Niile",
    showMore: "Gosi Karịa",
    showLess: "Gosi Obere",
    filter: "Ṅcha",
    sort: "Hazie",
    sortBy: "Hazie site na",
    priceLowestFirst: "Ọnụahịa: Obere ruo Nnukwu",
    priceHighestFirst: "Ọnụahịa: Nnukwu ruo Obere",
    newest: "Ọhụrụ kachasị",
    popular: "A ma ama",
    inStoreOnly: "N'ụlọ ahịa naanị",
    onlineOnly: "N'ịntanetị naanị",
  },
  
  pt: {
    // Portuguese
    welcome: "Bem-vindo",
    signIn: "Entrar",
    signUp: "Cadastrar",
    signOut: "Sair",
    email: "E-mail",
    password: "Senha",
    confirmPassword: "Confirmar Senha",
    name: "Nome",
    phone: "Telefone",
    save: "Salvar",
    cancel: "Cancelar",
    back: "Voltar",
    next: "Próximo",
    loading: "Carregando...",
    search: "Buscar",
    cart: "Carrinho",
    checkout: "Finalizar",
    total: "Total",
    subtotal: "Subtotal",
    tax: "Imposto",
    quantity: "Quantidade",
    item: "Item",
    items: "Itens",
    home: "Início",
    shop: "Loja",
    account: "Conta",
    settings: "Configurações",
    help: "Ajuda",
    about: "Sobre",
    contact: "Contato",
    
    createAccount: "Criar Conta",
    forgotPassword: "Esqueceu a Senha?",
    resetPassword: "Redefinir Senha",
    sendResetCode: "Enviar Código",
    enterEmail: "Digite seu e-mail",
    enterPassword: "Digite sua senha",
    alreadyHaveAccount: "Já tem uma conta?",
    dontHaveAccount: "Não tem uma conta?",
    passwordResetSent: "Código de redefinição enviado",
    loginSuccess: "Bem-vindo de volta!",
    logoutSuccess: "Você saiu",
    accountCreated: "Conta criada com sucesso!",
    invalidCredentials: "E-mail ou senha inválidos",
    
    selectLanguage: "Selecionar Idioma",
    language: "Idioma",
    
    allCategories: "Todas as Categorias",
    all: "Tudo",
    produce: "Frutas e Verduras",
    meats: "Carnes",
    seafood: "Frutos do Mar",
    spices: "Especiarias",
    grains: "Grãos",
    beverages: "Bebidas",
    snacks: "Lanches",
    canned: "Enlatados",
    frozen: "Congelados",
    sauces: "Molhos",
    dairy: "Laticínios",
    oils: "Óleos",
    
    products: "Produtos",
    addToCart: "Adicionar ao Carrinho",
    addedToCart: "Adicionado ao carrinho!",
    outOfStock: "Esgotado",
    inStock: "Em Estoque",
    price: "Preço",
    category: "Categoria",
    searchProducts: "Buscar produtos...",
    noProductsFound: "Nenhum produto encontrado",
    productDetails: "Detalhes do Produto",
    viewDetails: "Ver Detalhes",
    
    emptyCart: "Seu carrinho está vazio",
    removeFromCart: "Remover",
    continueShopping: "Continuar Comprando",
    proceedToCheckout: "Finalizar Compra",
    cartUpdated: "Carrinho atualizado",
    itemRemoved: "Item removido do carrinho",
    clearCart: "Limpar Carrinho",
    cartCleared: "Carrinho limpo",
    
    myOrders: "Meus Pedidos",
    orderHistory: "Histórico de Pedidos",
    orderStatus: "Status do Pedido",
    orderNumber: "Número do Pedido",
    orderDate: "Data do Pedido",
    orderTotal: "Total do Pedido",
    trackOrder: "Rastrear Pedido",
    reorder: "Pedir Novamente",
    noOrders: "Sem pedidos ainda",
    
    pending: "Pendente",
    confirmed: "Confirmado",
    preparing: "Preparando",
    ready: "Pronto para Retirada",
    outForDelivery: "Saiu para Entrega",
    delivered: "Entregue",
    completed: "Concluído",
    cancelled: "Cancelado",
    refunded: "Reembolsado",
    
    paymentMethod: "Método de Pagamento",
    creditCard: "Cartão de Crédito/Débito",
    cashOnDelivery: "Pagamento na Entrega",
    cashOnPickup: "Pagamento na Retirada",
    giftCardPayment: "Cartão Presente",
    payNow: "Pagar Agora",
    paymentSuccess: "Pagamento realizado!",
    paymentFailed: "Pagamento falhou. Tente novamente.",
    
    delivery: "Entrega",
    pickup: "Retirada",
    deliveryAddress: "Endereço de Entrega",
    pickupLocation: "Local de Retirada",
    deliveryTime: "Hora da Entrega",
    pickupTime: "Hora da Retirada",
    estimatedDelivery: "Entrega Estimada",
    freeDelivery: "Frete Grátis",
    deliveryFee: "Taxa de Entrega",
    
    adminPanel: "Painel Admin",
    dashboard: "Painel",
    manageProducts: "Gerenciar Produtos",
    manageOrders: "Gerenciar Pedidos",
    manageUsers: "Gerenciar Usuários",
    addProduct: "Adicionar Produto",
    editProduct: "Editar Produto",
    deleteProduct: "Excluir Produto",
    productAdded: "Produto adicionado!",
    productUpdated: "Produto atualizado!",
    productDeleted: "Produto excluído!",
    uploadImage: "Enviar Imagem",
    refineWithAI: "Refinar com IA",
    refiningImage: "Refinando imagem com IA...",
    imageRefined: "Imagem refinada!",
    imageUploadFailed: "Falha ao enviar imagem",
    
    giftCards: "Cartões Presente",
    buyGiftCard: "Comprar Cartão",
    redeemGiftCard: "Resgatar Cartão",
    giftCardAmount: "Valor do Cartão",
    recipientEmail: "E-mail do Destinatário",
    recipientName: "Nome do Destinatário",
    senderName: "Seu Nome",
    personalMessage: "Mensagem Pessoal",
    giftCardPurchased: "Cartão comprado!",
    giftCardRedeemed: "Cartão resgatado!",
    invalidGiftCard: "Código de cartão inválido",
    giftCardBalance: "Saldo do Cartão",
    
    scanBarcode: "Escanear Código",
    scanToCart: "Escanear para Adicionar",
    scanToEdit: "Escanear para Editar",
    scanAnother: "Escanear Outro",
    productFound: "Produto Encontrado!",
    productNotFound: "Produto Não Encontrado",
    cameraPermissionDenied: "Permissão de câmera negada",
    cameraError: "Erro de câmera",
    
    pictures2cart: "FOTOS2CARRINHO",
    picture2edit: "FOTO2EDITAR",
    takePhoto: "Tirar Foto",
    uploadPhoto: "Enviar Foto",
    analyzing: "Analisando...",
    tryAgain: "Tentar Novamente",
    aiSearching: "IA buscando...",
    
    success: "Sucesso",
    error: "Erro",
    warning: "Aviso",
    info: "Info",
    confirmAction: "Tem certeza?",
    yes: "Sim",
    no: "Não",
    exit: "Sair",
    close: "Fechar",
    confirm: "Confirmar",
    delete: "Excluir",
    edit: "Editar",
    view: "Ver",
    share: "Compartilhar",
    copy: "Copiar",
    copied: "Copiado!",
    saved: "Salvo!",
    updated: "Atualizado!",
    deleted: "Excluído!",
    failed: "Falhou",
    tryAgainLater: "Tente mais tarde",
    somethingWentWrong: "Algo deu errado",
    networkError: "Erro de rede. Verifique sua conexão.",
    
    driverDashboard: "Painel do Motorista",
    availableOrders: "Pedidos Disponíveis",
    myDeliveries: "Minhas Entregas",
    acceptOrder: "Aceitar Pedido",
    startDelivery: "Iniciar Entrega",
    completeDelivery: "Concluir Entrega",
    orderAccepted: "Pedido aceito!",
    deliveryStarted: "Entrega iniciada!",
    deliveryCompleted: "Entrega concluída!",
    
    notifications: "Notificações",
    noNotifications: "Sem notificações",
    markAsRead: "Marcar como lido",
    clearAll: "Limpar tudo",
    newOrder: "Novo Pedido",
    orderUpdate: "Atualização de Pedido",
    promotion: "Promoção",
    
    storeHours: "Horário da Loja",
    storeLocation: "Localização da Loja",
    contactUs: "Fale Conosco",
    aboutUs: "Sobre Nós",
    
    seeAll: "Ver Tudo",
    showMore: "Mostrar Mais",
    showLess: "Mostrar Menos",
    filter: "Filtrar",
    sort: "Ordenar",
    sortBy: "Ordenar Por",
    priceLowestFirst: "Preço: Menor para Maior",
    priceHighestFirst: "Preço: Maior para Menor",
    newest: "Mais Novo",
    popular: "Popular",
    inStoreOnly: "Apenas na Loja",
    onlineOnly: "Apenas Online",
  },
};

// Language metadata
export const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'ht', name: 'Kreyòl Ayisyen', flag: '🇭🇹' },
  { code: 'ha', name: 'Hausa', flag: '🇳🇬' },
  { code: 'yo', name: 'Yorùbá', flag: '🇳🇬' },
  { code: 'ig', name: 'Igbo', flag: '🇳🇬' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
];

// Create context
const LanguageContext = createContext();

// Local storage key
const LANGUAGE_STORAGE_KEY = 'asap247_language';

// Provider component
export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Try to get from localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      return stored || 'en';
    }
    return 'en';
  });

  // Get translation function
  const t = useCallback((key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  }, [language]);

  // Get category translation
  const tCategory = useCallback((categoryId) => {
    const key = categoryId?.toLowerCase();
    return translations[language]?.[key] || translations['en']?.[key] || categoryId;
  }, [language]);

  // Set language and persist
  const setLanguage = useCallback((newLang) => {
    if (translations[newLang]) {
      setLanguageState(newLang);
      if (typeof window !== 'undefined') {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
      }
    }
  }, []);

  // Sync with user account if logged in
  const syncWithUser = useCallback((userLanguage) => {
    if (userLanguage && translations[userLanguage]) {
      setLanguageState(userLanguage);
      if (typeof window !== 'undefined') {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, userLanguage);
      }
    }
  }, []);

  const value = {
    language,
    setLanguage,
    syncWithUser,
    t,
    tCategory,
    languages: LANGUAGES,
    translations: translations[language] || translations['en'],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
